import classNames from "classnames";
import React, { Dispatch, SetStateAction } from "react";

import { SettingsIconButton } from "../../../../components/button/SettingsIconButton";
import { SettingsCard } from "../../../../components/card/SettingsCard";
import { CloseSvg } from "../../../../components/svg/CloseSvg";
import { StatusName } from "../../../../factories/statuses/enums/status.enum";
import { StatusModel } from "../../../../factories/statuses/models/status.model";
import { WorkTypeStatusesModel } from "../../../../factories/work-type/models/work-type.model";
import { UpdateWorkTypeStatusModel } from "../../../../slices/WorkTypeSlice";
import { SelectStatusDropdown } from "./SelectStatusDropdown";

type Props = {
  status: WorkTypeStatusesModel;
  allStatuses: StatusModel[];
  workTypeStatus: UpdateWorkTypeStatusModel | undefined;
  workTypeId?: string;
  setWorkTypeStatuses: Dispatch<SetStateAction<UpdateWorkTypeStatusModel[]>>;
  getStatuses: () => Promise<StatusModel[] | undefined>;
  isLoadingStatuses: boolean;
};

const colors: Partial<
  Record<StatusName, { icon: string; iconShadow: string; text: string }>
> = {
  [StatusName.Ready]: {
    icon: "bg-[#3FABE5]",
    iconShadow: "bg-[#3FABE533]",
    text: "text-[#0E5C86]",
  },
  [StatusName.InProgress]: {
    icon: "bg-[#FAAF0E]",
    iconShadow: "bg-[#FAAF0E33]",
    text: "text-[#C58700]",
  },
  [StatusName.Waiting]: {
    icon: "bg-[#FF833D]",
    iconShadow: "bg-[#FF833D33]",
    text: "text-[#BC4B0C]",
  },
  [StatusName.Completed]: {
    icon: "bg-[#3ABA6D]",
    iconShadow: "bg-[#3ABA6D33]",
    text: "text-[#2D9D60]",
  },
  [StatusName.Scheduled]: {
    icon: "bg-[#1f2937]",
    iconShadow: "bg-[#1f293733]",
    text: "text-[#1f2937]",
  },
};

export const StatusesCard: React.FC<Props> = ({
  status,
  allStatuses,
  workTypeStatus,
  workTypeId,
  setWorkTypeStatuses,
  getStatuses,
  isLoadingStatuses,
}) => {
  const subStatuses =
    (allStatuses || []).find((item) => item.name === status.name)
      ?.workTypeSubStatuses || [];

  const selectedSubStatusIds =
    workTypeStatus?.workTypeSubStatusIds ||
    status.serviceSubStatuses.map((item) => item.worktypeSubStatusId);

  const assignedSubStatuses = selectedSubStatusIds.map((id) => {
    const availableSubStatus = subStatuses.find((item) => item.id === id);
    const assignedSubStatus = status.serviceSubStatuses.find(
      (item) => item.worktypeSubStatusId === id,
    );

    return {
      id,
      subStatus: availableSubStatus?.subStatus || assignedSubStatus?.subStatus,
    };
  });

  const removeSubStatus = (id: string) => {
    setWorkTypeStatuses((prev) =>
      prev.map((item) =>
        item.workTypeStatusId === status.workTypeStatusId
          ? {
              ...item,
              workTypeSubStatusIds: item.workTypeSubStatusIds.filter(
                (subStatusId) => subStatusId !== id,
              ),
            }
          : item,
      ),
    );
  };

  return (
    <div>
      <SettingsCard>
        <div className={"flex items-center justify-between gap-2 p-4"}>
          <div className={"flex items-center gap-1.5"}>
            <div
              className={classNames(
                "flex h-6 w-6 items-center justify-center rounded-full",
                colors[status.name]?.iconShadow,
              )}>
              <div
                className={classNames(
                  "h-3 w-3 rounded-full",
                  colors[status.name]?.icon,
                )}></div>
            </div>
            <div
              className={classNames(
                "text-lg font-semibold",
                colors[status.name]?.text,
              )}>
              {status.name}
            </div>
          </div>
          <div
            className={
              "flex h-6 w-fit min-w-[24px] items-center justify-center rounded-full bg-[#E4E9F2] px-2 text-sm font-semibold text-[#3F5266]"
            }>
            {assignedSubStatuses.length}
          </div>
        </div>
        <div className={"transition-all duration-300"}>
          <div>
            {assignedSubStatuses.map((item) => (
              <div
                key={item.id}
                className={
                  "group grid h-12 grid-cols-[1fr_max-content] items-center gap-1 border-b border-[#E4E9F2] pl-4 pr-1 last:border-none"
                }>
                <div
                  title={item.subStatus}
                  className={"truncate text-base font-medium text-[#3F5266]"}>
                  {item.subStatus}
                </div>
                <div
                  className={
                    "pointer-events-none opacity-0 transition-opacity duration-100 group-hover:pointer-events-auto group-hover:opacity-100"
                  }>
                  <SettingsIconButton
                    icon={<CloseSvg />}
                    onClick={() => removeSubStatus(item.id)}
                    size={"small"}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <SelectStatusDropdown
          subStatuses={subStatuses}
          workTypeStatus={workTypeStatus}
          setWorkTypeStatuses={setWorkTypeStatuses}
          getStatuses={getStatuses}
          isLoadingStatuses={isLoadingStatuses}
          workTypeId={workTypeId}
        />
      </SettingsCard>
    </div>
  );
};
